import React, { useEffect } from "react";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { ChevronDown, Mail, Phone } from "lucide-react";

const faqs = [
  {
    question: "How can I book movie tickets at Grand Cinemaz?",
    answer: "You can book tickets via our website or book my show. Simply select your movie, preferred showtime, and seats, then proceed to secure checkout. Booking is seamless and confirmed instantly.",
  },
  {
    question: "Do you offer online seat selection?",
    answer: "Yes, you can choose your preferred seats through our interactive seat map during the booking process, ensuring a personalized viewing experience.",
  },
  {
    question: "What payment methods are accepted?",
    answer: "We support UPI, net banking, credit/debit cards, and digital wallets like Google Pay, Paytm, PhonePe, and more.",
  },
  {
    question: "Are refunds available for canceled or missed shows?",
    answer: "If a show is canceled by us, a full refund is processed within 5–7 working days. Unfortunately, missed shows are non-refundable per policy.",
  },
  {
    question: "Can I modify my ticket after booking?",
    answer: "Currently, modifying tickets (e.g., changing seat or time) after confirmation isn’t supported. For exceptional cases, reach out to our helpdesk.",
  },
  {
    question: "What are the benefits of Grand Cinemaz Club Membership?",
    answer: "Members enjoy early access to bookings, special offers, discounts on snacks, and exclusive lounge access at select cinemas.",
  },
  {
    question: "Do you provide special facilities for differently-abled guests?",
    answer: "Yes. Our cinemas are wheelchair accessible and equipped with dedicated seating and staff assistance. Please notify our team in advance for support.",
  },
  {
    question: "Can I host private screenings or corporate events?",
    answer: "Absolutely! We offer premium event services for private screenings, birthdays, and business gatherings. Contact our hospitality manager to customize your experience.",
  },
  {
    question: "Is parking available at all Grand Cinemaz locations?",
    answer: "Yes, we provide ample parking at all our theatres. Premium valet parking is also available at select locations.",
  },
  {
    question: "How do I reach customer support?",
    answer: "You can email us at support@grandcinemaz.in or call our helpline at +91-98765-43210. We’re here to help 7 days a week.",
  },
];

const FAQsPage = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 py-16 px-6 lg:px-24">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-4xl mx-auto text-center mb-10"
      >
        <h1 className="text-5xl font-extrabold text-gray-900 mb-4">
          FAQs – Grand Cinemaz
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Find answers to common queries and learn how to make the most of your movie-going experience with <strong>Grand Cinemaz</strong>, proudly operated by {" "}
          <a
            href="https://dbgroup.dodunsoftsolutions.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-500 font-semibold hover:underline"
          >
            Dev Bhoomi Group Comapnies
          </a>

        </p>
      </motion.div>

      {/* FAQ Entries */}
      <div className="max-w-5xl mx-auto space-y-6">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            data-aos="fade-up"
            className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200 "
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-start space-x-4">
              <ChevronDown className="mt-1 text-amber-500 shrink-0" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 ">{faq.question}</h2>
                <p className="mt-2 text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Call to Action */}
      <div
        className="mt-20 bg-gradient-to-r from-red-50 to-red-100 text-black py-10 px-6 rounded-2xl shadow-xl text-center"
        data-aos="fade-up"
      >
        <h3 className="text-3xl font-bold mb-4">Still have questions?</h3>
        <p className="text-lg mb-6">We're here to help. Reach out to our customer experience team for any assistance.</p>
        <div className="flex justify-center items-center gap-6 text-lg font-medium">
          <a href="tel:+916230301476" className="inline-flex items-center text-amber-600 gap-2 hover:underline">
            <Phone className="w-5 h-5" />
            +91 62303 01476
          </a>
          <span>|</span>
          <a href="tel:+916230295069" className="inline-flex items-center text-amber-600 gap-2 hover:underline">
            <Phone className="w-5 h-5" />
            +91 62302 95069
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQsPage;
